/**
 * VerifyEmailPage — autenticazione Phase 5 (IMPL-P5-011).
 *
 * UC-006 email verification. Reads the `?token=…` query param via
 * `useSearchParams`, calls `authClient.verifyEmail(token)` exactly once on
 * mount, and renders one of three terminal screens:
 *   - loading: AuthStatusPage with `Loader2` (spinning) icon
 *   - success: tone=success + "Accedi alla piattaforma" CTA
 *   - error:   tone=error + "Richiedi nuovo link" CTA → `/accedi`
 *
 * The `useEffect` runs once thanks to a guard ref so React 18 StrictMode's
 * double-mount in dev doesn't fire a second verify call.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { AuthStatusPage } from '../components/AuthStatusPage';
import { authClient, isAuthClientError } from '../lib/auth-client';
import { serverErrorToItalian } from '../lib/errors';

type VerifyState =
  | { phase: 'loading' }
  | { phase: 'success' }
  | { phase: 'error'; code: string };

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<VerifyState>({ phase: 'loading' });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setState({ phase: 'error', code: 'INVALID_TOKEN' });
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        await authClient.verifyEmail(token);
        if (!cancelled) setState({ phase: 'success' });
      } catch (err: unknown) {
        if (cancelled) return;
        const code = isAuthClientError(err) ? err.code : 'UNKNOWN';
        setState({ phase: 'error', code });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.phase === 'loading') {
    return (
      <AuthStatusPage
        icon={Loader2}
        tone="info"
        title="Verifica in corso…"
        description="Stiamo verificando la tua email. Resta su questa pagina."
      />
    );
  }

  if (state.phase === 'success') {
    return (
      <AuthStatusPage
        icon={CheckCircle2}
        tone="success"
        title="Email verificata"
        description="Il tuo account è stato attivato. Puoi accedere al portale."
        primaryAction={{ label: 'Accedi alla piattaforma', to: '/accedi' }}
      />
    );
  }

  return (
    <AuthStatusPage
      icon={XCircle}
      tone="error"
      title="Verifica non riuscita"
      description={serverErrorToItalian(state.code)}
      primaryAction={{ label: 'Richiedi nuovo link', to: '/accedi' }}
    />
  );
}
